/**
 * The Header bell's badge: how many notifications in the signed-in user's own
 * inbox are unread (GET /api/notifications/count, owner-only). The inbox holds
 * what the notification router put there (src/notifications/): every message
 * whose chosen channel is In-App, and the record of one no other channel carried.
 *
 * Refreshes every three minutes while the tab is visible, when it becomes
 * visible again, and at once on `notifyNotificationsChanged()` (the bell's
 * own mark-read actions), so the badge never lags the list.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';

export const NOTIFICATIONS_CHANGED_EVENT = 'compass:notifications-changed';
/** At most every three minutes; the visibility refresh and notifyNotificationsChanged() keep it current. */
export const NOTIFICATION_COUNT_POLL_MS = 3 * 60 * 1000;

/** Tell every bell that the inbox changed (an item or all of them marked read). */
export function notifyNotificationsChanged(): void {
  window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
}

/** @param enabled `false` while signed out (the API answers 401). */
export function useNotificationCount(enabled = true): number {
  const [count, setCount] = useState(0);

  const check = useCallback(async () => {
    try {
      setCount(await api.getNotificationCount());
    } catch {
      // non-blocking: the badge stays as it was until the next tick
    }
  }, []);

  useEffect(() => {
    if (!enabled) { setCount(0); return; }
    void check();
    const timer = setInterval(() => { if (document.visibilityState !== 'hidden') void check(); }, NOTIFICATION_COUNT_POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') void check(); };
    const onChanged = () => { void check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    };
  }, [enabled, check]);

  return count;
}
